import createCrudService from '@core/api/create-crud-service';
import { queryClient } from '@src/core/api/query-client';
import { request } from '@src/core/api/request';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import type { TBaseQuery } from '@src/shared/types/base-query';
import type { TNotification } from '@src/shared/types/notification';

export type TNotificationQuery = TBaseQuery;

type TMarkAsReadBody = {
  ids: TNotification['id'][];
};

const notificationApi = {
  ...createCrudService<
    TNotification,
    TNotificationQuery,
    Partial<TNotification>
  >({
    queryKey: [QUERY_KEYS.getNotifications],
    url: 'notifications',
  }),
  useUnreadCount: (options?: Partial<UseQueryOptions<{ count: number }>>) =>
    useQuery({
      queryKey: [QUERY_KEYS.getNotifications, 'count'],
      queryFn: async () => {
        const response = await request<{ count: number }>({
          url: 'notifications/unread_count/',
          method: 'GET',
        });
        return response;
      },
      staleTime: 60000,
      ...options,
    }),
  useMarkAsRead: (options?: UseMutationOptions<void, Error, TMarkAsReadBody>) =>
    useMutation<void, Error, TMarkAsReadBody>({
      mutationKey: [QUERY_KEYS.getNotifications, 'mark-as-read'],
      mutationFn: async (body) => {
        await request({
          url: 'notifications/mark-as-read/',
          method: 'POST',
          body,
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getNotifications],
        });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getNotifications, 'count'],
        });
      },
      ...options,
    }),
  useMarkAllAsRead: (options?: UseMutationOptions<void, Error, unknown>) =>
    useMutation<void, Error, unknown>({
      mutationKey: [QUERY_KEYS.getNotifications, 'mark-all-read'],
      mutationFn: async (body) => {
        await request({
          url: 'notifications/mark-all-read/',
          method: 'POST',
          body,
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getNotifications],
        });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getNotifications, 'count'],
        });
      },
      ...options,
    }),
};

export default notificationApi;
